'use client'

import { useCallback } from 'react'
import { Toast } from '@skop-app/lib-front'

import { useUserContext } from '@/context'
import type { ProjectPermissions } from '@/features/project/project.permissions'
import {
  useAssignAvailableSeat,
  useCreateOrganization,
  useCreateOrganizationMember,
  useCreateOrganizationWithRecovery,
  useDeleteMember,
  useReassignSeat,
  useResumeOrganizationCreation,
  useUpdateOrganization,
  useUpdateOrganizationMember,
} from '@/services/mutations/team'
import { fetchOrganizationMetadata } from '@/services/queries/team'
import { ORG_METADATA_GALLERY_BG_REMOVAL_KEYS } from '@/constants/gallery.metadata'
import type { OrganizationMetadataEntry } from '@/utils/organizationMetadata'

import {
  canCreateTeams,
  canEditTeamAuthorizationPolicy,
  canManageOrganizationSettings,
  canManageProjectMembers,
  canManageTeamMembers,
  canMutateDomain,
} from './helpers'
import { useGuardedMutation } from './guardedMutation'

export type GalleryBackgroundRemovalColorsMetadataUpdateVariables = {
  organizationId: string
  previousMetadata: OrganizationMetadataEntry[]
  nextMetadata: OrganizationMetadataEntry[]
}

const galleryBackgroundRemovalMetadataKeys = new Set<string>(
  ORG_METADATA_GALLERY_BG_REMOVAL_KEYS,
)

const serializeMetadataEntry = (entry: OrganizationMetadataEntry) =>
  JSON.stringify({
    key: entry.key,
    type: entry.type,
    value: entry.value,
  })

const metadataSignature = (metadata: OrganizationMetadataEntry[]) =>
  metadata.map(serializeMetadataEntry).sort()

const nonGalleryMetadataSignature = (metadata: OrganizationMetadataEntry[]) =>
  metadata
    .filter((entry) => !galleryBackgroundRemovalMetadataKeys.has(entry.key))
    .map(serializeMetadataEntry)
    .sort()

const metadataSignaturesMatch = (
  left: OrganizationMetadataEntry[],
  right: OrganizationMetadataEntry[],
) => {
  const leftSignature = metadataSignature(left)
  const rightSignature = metadataSignature(right)

  return (
    leftSignature.length === rightSignature.length &&
    leftSignature.every((entry, index) => entry === rightSignature[index])
  )
}

const onlyChangesGalleryBackgroundRemovalColorsMetadata = ({
  previousMetadata,
  nextMetadata,
}: Pick<
  GalleryBackgroundRemovalColorsMetadataUpdateVariables,
  'previousMetadata' | 'nextMetadata'
>) => {
  const previousSignature = nonGalleryMetadataSignature(previousMetadata)
  const nextSignature = nonGalleryMetadataSignature(nextMetadata)

  return (
    previousSignature.length === nextSignature.length &&
    previousSignature.every((entry, index) => entry === nextSignature[index])
  )
}

const canMutateProjectMembers = (params: {
  projectID?: string | null
  targetUserId?: string | null
  currentUserId?: string | null
  authorizationCanManageMembers: boolean
  projectPermissions?: Pick<ProjectPermissions, 'canLeaveProject'>
  isCurrentUser?: boolean
}) => {
  if (!params.projectID) {
    const leaveSelf =
      params.isCurrentUser &&
      params.targetUserId != null &&
      params.currentUserId != null &&
      params.targetUserId === params.currentUserId
    return params.authorizationCanManageMembers || leaveSelf
  }

  if (
    params.targetUserId &&
    params.currentUserId &&
    params.targetUserId === params.currentUserId &&
    params.projectPermissions?.canLeaveProject
  ) {
    return true
  }

  return params.authorizationCanManageMembers
}

export const useAuthorizedCreateOrganization = () => {
  const mutation = useCreateOrganization()
  const { authorization } = useUserContext()

  return useGuardedMutation(
    mutation,
    canCreateTeams(authorization),
    "Vous n'avez pas l'autorisation de créer une équipe.",
  )
}

export const useAuthorizedCreateOrganizationWithRecovery = () => {
  const mutation = useCreateOrganizationWithRecovery()
  const { authorization } = useUserContext()

  return useGuardedMutation(
    mutation,
    canCreateTeams(authorization),
    "Vous n'avez pas l'autorisation de créer une équipe.",
  )
}

export const useAuthorizedResumeOrganizationCreation = () => {
  const mutation = useResumeOrganizationCreation()
  const { authorization } = useUserContext()

  return useGuardedMutation(
    mutation,
    canCreateTeams(authorization),
    "Vous n'avez pas l'autorisation de reprendre la création d'une équipe.",
  )
}

export const useAuthorizedUpdateOrganization = (params?: {
  permission?: 'organization' | 'settings' | 'permissions'
}) => {
  const mutation = useUpdateOrganization()
  const { authorization } = useUserContext()

  const canExecute =
    params?.permission === 'settings'
      ? canManageOrganizationSettings(authorization)
      : params?.permission === 'permissions'
        ? canEditTeamAuthorizationPolicy(authorization)
        : Boolean(authorization?.canManageOrganization)

  const message =
    params?.permission === 'settings'
      ? "Vous n'avez pas l'autorisation de modifier les paramètres de l'équipe."
      : params?.permission === 'permissions'
        ? "Vous n'avez pas l'autorisation de modifier les autorisations de l'équipe."
        : "Vous n'avez pas l'autorisation de gérer cette équipe."

  return useGuardedMutation(mutation, canExecute, message)
}

export const useAuthorizedUpdateGalleryBackgroundRemovalColorsMetadata = () => {
  const mutation = useUpdateOrganization()
  const { mutate: updateOrganization, mutateAsync: updateOrganizationAsync } =
    mutation
  const { authorization, organizationScope } = useUserContext()
  const selectedOrganizationId = organizationScope?.selectedOrganizationId
  const selectedOrganization = organizationScope?.selectedOrganization
  const selectedOrganizationName =
    selectedOrganizationId &&
    selectedOrganization?.id === selectedOrganizationId
      ? selectedOrganization.name
      : undefined
  const canExecute = Boolean(
    selectedOrganizationId &&
    selectedOrganizationName &&
    canMutateDomain(authorization, 'products', 'canUpdate'),
  )
  const message =
    "Vous n'avez pas l'autorisation de modifier les préférences de détourage."

  const validateLocal = useCallback(
    (variables: GalleryBackgroundRemovalColorsMetadataUpdateVariables) => {
      if (
        !canExecute ||
        !selectedOrganizationId ||
        !selectedOrganizationName ||
        variables.organizationId !== selectedOrganizationId
      ) {
        Toast.error(message)
        return false
      }

      if (!onlyChangesGalleryBackgroundRemovalColorsMetadata(variables)) {
        Toast.error(message)
        return false
      }

      return true
    },
    [canExecute, message, selectedOrganizationId, selectedOrganizationName],
  )

  const validate = useCallback(
    async (
      variables: GalleryBackgroundRemovalColorsMetadataUpdateVariables,
    ) => {
      if (!validateLocal(variables)) return false

      try {
        const currentMetadata = await fetchOrganizationMetadata({
          id: variables.organizationId,
        })

        if (
          !metadataSignaturesMatch(currentMetadata, variables.previousMetadata)
        ) {
          Toast.error(message)
          return false
        }
      } catch {
        Toast.error(message)
        return false
      }

      return true
    },
    [message, validateLocal],
  )

  const mutate = useCallback(
    (
      variables: GalleryBackgroundRemovalColorsMetadataUpdateVariables,
      options?: Parameters<typeof updateOrganization>[1],
    ) => {
      void (async () => {
        if (!(await validate(variables))) return

        updateOrganization(
          {
            organization: {
              id: variables.organizationId,
              name: selectedOrganizationName,
              metadata: variables.nextMetadata,
            },
          },
          options,
        )
      })()
    },
    [selectedOrganizationName, updateOrganization, validate],
  )

  const mutateAsync = useCallback(
    async (
      variables: GalleryBackgroundRemovalColorsMetadataUpdateVariables,
      options?: Parameters<typeof updateOrganizationAsync>[1],
    ) => {
      if (!(await validate(variables))) {
        throw new Error(message)
      }

      return updateOrganizationAsync(
        {
          organization: {
            id: variables.organizationId,
            name: selectedOrganizationName,
            metadata: variables.nextMetadata,
          },
        },
        options,
      )
    },
    [message, selectedOrganizationName, updateOrganizationAsync, validate],
  )

  return {
    ...mutation,
    mutate,
    mutateAsync,
  }
}

export const useAuthorizedCreateOrganizationMember = (params?: {
  projectPermissions?: ProjectPermissions
}) => {
  const mutation = useCreateOrganizationMember()
  const { authorization } = useUserContext()

  return useGuardedMutation(
    mutation,
    params?.projectPermissions
      ? canManageProjectMembers(authorization, params.projectPermissions)
      : canManageTeamMembers(authorization),
    "Vous n'avez pas l'autorisation de gérer les membres.",
  )
}

export const useAuthorizedUpdateOrganizationMember = (params?: {
  projectPermissions?: ProjectPermissions
}) => {
  const mutation = useUpdateOrganizationMember()
  const { authorization } = useUserContext()

  return useGuardedMutation(
    mutation,
    params?.projectPermissions
      ? canManageProjectMembers(authorization, params.projectPermissions)
      : canManageTeamMembers(authorization),
    "Vous n'avez pas l'autorisation de gérer les membres.",
  )
}

export const useAuthorizedReassignSeat = () => {
  const mutation = useReassignSeat()
  const { authorization, organizationScope } = useUserContext()
  return useGuardedMutation(
    mutation,
    Boolean(organizationScope.activeOrganizationId) &&
      canManageTeamMembers(authorization),
    "Vous n'avez pas l'autorisation de gérer les sièges.",
  )
}

export const useAuthorizedAssignAvailableSeat = () => {
  const mutation = useAssignAvailableSeat()
  const { authorization, organizationScope } = useUserContext()
  return useGuardedMutation(
    mutation,
    Boolean(organizationScope.activeOrganizationId) &&
      canManageTeamMembers(authorization),
    "Vous n'avez pas l'autorisation de gérer les sièges.",
  )
}

export const useAuthorizedDeleteMember = (params?: {
  projectPermissions?: ProjectPermissions
}) => {
  const mutation = useDeleteMember()
  const { authorization, user } = useUserContext()
  const authorizationCanManageMembers = params?.projectPermissions
    ? canManageProjectMembers(authorization, params.projectPermissions)
    : canManageTeamMembers(authorization)

  const mutate = useCallback(
    (...args: Parameters<typeof mutation.mutate>) => {
      const [variables, options] = args
      const targetUserId = variables.memberUserId
      const canExecute = canMutateProjectMembers({
        projectID: 'projectID' in variables ? variables.projectID : undefined,
        targetUserId,
        currentUserId: user?.id,
        authorizationCanManageMembers,
        projectPermissions: params?.projectPermissions,
        isCurrentUser:
          'isCurrentUser' in variables ? variables.isCurrentUser : undefined,
      })

      if (!canExecute) {
        Toast.error("Vous n'avez pas l'autorisation de supprimer ce membre.")
        return
      }

      mutation.mutate(variables, options)
    },
    [
      authorizationCanManageMembers,
      mutation,
      params?.projectPermissions,
      user?.id,
    ],
  )

  const mutateAsync = useCallback(
    async (...args: Parameters<NonNullable<typeof mutation.mutateAsync>>) => {
      const [variables, options] = args
      const targetUserId = variables.memberUserId
      const canExecute = canMutateProjectMembers({
        projectID: 'projectID' in variables ? variables.projectID : undefined,
        targetUserId,
        currentUserId: user?.id,
        authorizationCanManageMembers,
        projectPermissions: params?.projectPermissions,
        isCurrentUser:
          'isCurrentUser' in variables ? variables.isCurrentUser : undefined,
      })

      if (!canExecute) {
        const message = "Vous n'avez pas l'autorisation de supprimer ce membre."
        Toast.error(message)
        throw new Error(message)
      }

      return mutation.mutateAsync?.(variables, options)
    },
    [
      authorizationCanManageMembers,
      mutation,
      params?.projectPermissions,
      user?.id,
    ],
  )

  return {
    ...mutation,
    mutate,
    mutateAsync,
  }
}
